import * as React from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import useProductImages from 'hooks/useProductImages';

import styles from './FuzzyFindDialog.module.css';
import { buildNameIndex, CONFIDENT_SCORE, NameEntry, Suggestion, suggestNames } from './nameRepair';
import NameRepairList, { RepairRow } from './NameRepairList';
import type { CatalogProduct } from '../ProductPicker';

/** Suggestions follow the typing after this pause, so results don't re-rank on every keystroke. */
const SETTLE_MS = 250;

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

const namesIn = (text: string) =>
  Array.from(
    new Set(
      text
        .split(/[\n\t]/)
        .map((n) => n.trim())
        .filter(Boolean),
    ),
  );

export interface FuzzyFindDialogProps {
  open: boolean;
  onClose: () => void;
  catalog: CatalogProduct[];
  onAdd: (products: CatalogProduct[]) => void;
  /** Names to start with, e.g. the ones a paste couldn't match exactly. */
  initialNames?: string[];
  /** Names already in the set; they're reported but not added again. */
  addedNames?: ReadonlySet<string>;
}

/**
 * Finds products for approximate names (misspellings, missing words, other
 * punctuation): each name gets the closest catalog products to choose from,
 * with images and metadata. Confident matches are preselected.
 */
const FuzzyFindDialog: React.FC<FuzzyFindDialogProps> = ({
  open,
  onClose,
  catalog,
  onAdd,
  initialNames,
  addedNames,
}) => {
  const [text, setText] = React.useState('');
  // Explicit choices per name; `null` means the user skipped it.
  const [choices, setChoices] = React.useState<Record<string, string | null>>({});
  const [copied, setCopied] = React.useState(false);

  // The text the results are computed from; trails `text` by SETTLE_MS while typing.
  const [settledText, setSettledText] = React.useState('');

  React.useEffect(() => {
    if (!open || !initialNames?.length) return;
    const initial = initialNames.join('\n');
    setText(initial);
    setSettledText(initial);
  }, [open, initialNames]);

  React.useEffect(() => {
    if (text === settledText) return undefined;
    const timer = setTimeout(() => setSettledText(text), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [text, settledText]);

  // Scoring a name scans the whole catalog, so each name is scored once per catalog.
  const cache = React.useRef<{ catalog: CatalogProduct[]; index?: NameEntry[]; byName: Map<string, Suggestion[]> }>({
    catalog,
    byName: new Map(),
  });
  const names = React.useMemo(() => namesIn(settledText), [settledText]);
  const rows: RepairRow[] = React.useMemo(() => {
    if (cache.current.catalog !== catalog) cache.current = { catalog, byName: new Map() };
    const c = cache.current;
    return names.map((name) => {
      let found = c.byName.get(name);
      if (!found) {
        c.index ??= buildNameIndex(catalog);
        found = suggestNames(name, c.index);
        c.byName.set(name, found);
      }
      const best = found[0];
      const preselected = best && best.score >= CONFIDENT_SCORE ? best.entry.name : null;
      return { pasted: name, suggestions: found, chosen: name in choices ? choices[name] : preselected };
    });
  }, [names, catalog, choices]);

  const thumbnailIds = React.useMemo(
    () => Array.from(new Set(rows.flatMap((r) => r.suggestions.map((s) => s.entry.products[0].id)))),
    [rows],
  );
  const images = useProductImages(open ? thumbnailIds : []);

  const chosen = Array.from(
    new Map(
      rows.flatMap((r) => {
        const entry = r.suggestions.find((s) => s.entry.name === r.chosen)?.entry;
        return entry ? [[entry.name, entry] as const] : [];
      }),
    ).values(),
  );
  const toAdd = chosen.filter((e) => !addedNames?.has(e.name));
  const alreadyAdded = chosen.length - toAdd.length;
  const unresolved = rows.filter((r) => !r.chosen).map((r) => r.pasted);

  const close = () => {
    setText('');
    setSettledText('');
    setChoices({});
    setCopied(false);
    onClose();
  };

  const add = () => {
    onAdd(toAdd.flatMap((e) => e.products));
    close();
  };

  const copyUnresolved = async () => {
    try {
      await navigator.clipboard.writeText(unresolved.join('\n'));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    // A fixed height keeps the dialog still while results come and go.
    <Dialog open={open} onClose={close} fullWidth maxWidth="md" PaperProps={{ className: styles.paper }}>
      <DialogTitle>Fuzzy find products</DialogTitle>
      <DialogContent className={styles.content}>
        <DialogContentText>
          Type or paste approximate product names, one per line. Each gets the closest products in the catalog;
          confident matches are picked for you.
        </DialogContentText>
        <TextField
          autoFocus
          multiline
          minRows={3}
          maxRows={10}
          fullWidth
          margin="normal"
          label="Names to find"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setCopied(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && toAdd.length > 0) {
              e.preventDefault();
              add();
            }
          }}
          helperText="Press ⌘/Ctrl + Enter to add the chosen products."
        />
        {/* Always rendered, so the results below don't shift when it fills in. */}
        <Box className={styles.summaryRow}>
          <Typography variant="body2" aria-live="polite" className={styles.summary}>
            {rows.length > 0 && (
              <>
                {toAdd.length} chosen
                {alreadyAdded > 0 && `, ${alreadyAdded} already added`}
                {unresolved.length > 0 && `, ${unresolved.length} without a choice`}
              </>
            )}
          </Typography>
          {unresolved.length > 0 && (
            <Button size="small" onClick={copyUnresolved}>
              {copied ? 'Copied' : `Copy ${unresolved.length} without a choice`}
            </Button>
          )}
        </Box>
        <Box className={styles.results}>
          {rows.length > 0 ? (
            <NameRepairList
              rows={rows}
              images={images}
              onChoose={(name, product) => {
                setChoices((c) => ({ ...c, [name]: product }));
                setCopied(false);
              }}
              onAcceptAll={() =>
                setChoices((c) => ({
                  ...c,
                  ...Object.fromEntries(
                    rows
                      .filter((row) => row.suggestions.length)
                      .map((row) => [row.pasted, row.suggestions[0].entry.name]),
                  ),
                }))
              }
            />
          ) : (
            <Typography variant="body2" color="text.secondary" className={styles.empty}>
              Suggestions appear here as you type.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" disabled={toAdd.length === 0} onClick={add}>
          Add {plural(toAdd.length, 'product')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FuzzyFindDialog;
